export interface OrderItemDate {
  name: string;
  status: string;
  type: string;
  isCustomOffer?: boolean;
  price?: number;
  date: Date | undefined;
  endDate: Date | undefined;
}

function getItemPrice(price: number | string | undefined): number | undefined {
  const numericPrice = Number(price);

  return Number.isFinite(numericPrice) ? numericPrice : undefined;
}

/**
 * Extracts all order items with their dates into a consistent format.
 * This includes accommodation, bedbank, experiences, flights, insurance,
 * tours, cruises, car hire, and custom offers.
 */
export function getOrderItemDates(order: App.Order): Array<OrderItemDate> {
  const orderItemsAggregate: Array<OrderItemDate> = [];

  if (order.has_bedbank) {
    order.bedbank_items.forEach((bedbankItem) => {
      orderItemsAggregate.push({
        name: bedbankItem.offer?.name || '',
        type: bedbankItem.type,
        status: bedbankItem.status,
        price: getItemPrice(bedbankItem.total),
        date: new Date(bedbankItem.check_in),
        endDate: bedbankItem.check_out ? new Date(bedbankItem.check_out) : undefined,
      });
    });
  }

  if (order.accommodation_items.length > 0) {
    order.accommodation_items.forEach((accomodationItem) => {
      const endDateStr = accomodationItem.reservation?.check_out || accomodationItem.reservation?.end_date;
      orderItemsAggregate.push({
        // Room rates and offer titles are less useful than the hotel name when checking another booking.
        name:
          accomodationItem.fullReservation?.property?.name ||
          accomodationItem.offer_package?.property?.name ||
          accomodationItem.offer?.property?.name ||
          accomodationItem.offer?.name ||
          accomodationItem.name,
        status: accomodationItem.status,
        type: accomodationItem.type,
        price: getItemPrice(accomodationItem.total),
        date: new Date(accomodationItem.reservation?.check_in || accomodationItem.reservation?.start_date),
        endDate: endDateStr ? new Date(endDateStr) : undefined,
      });
    });
  }

  if (order.has_experience) {
    order.experience_items
      .filter((exp) => !exp.complimentary)
      .forEach((exp) => {
        // List responses do not consistently use the same field for an experience title.
        const experienceName =
          exp.title ||
          exp.ticket?.fareType ||
          exp.deal_options?.option?.name ||
          (exp as App.ExperienceItem & { name?: string }).name;
        orderItemsAggregate.push({
          name: experienceName,
          type: exp.type,
          status: exp.status,
          price: getItemPrice(exp.total),
          date: exp?.ticket?.date ? new Date(exp.ticket.date) : undefined,
          endDate: undefined,
        });
      });
  }

  if (order.has_flight) {
    order.flight_items.forEach((flightItem) => {
      // TODO: export appropriate types from backend service and use it here
      if (flightItem.journey?.departing?.flights?.length) {
        const departingFlights = flightItem.journey.departing.flights;
        const firstFlight = departingFlights[0];
        const lastFlight = departingFlights[departingFlights.length - 1];
        const departureAirport = firstFlight.departing_airport_name || firstFlight.departing_airport;
        const arrivalAirport = lastFlight.arrival_airport_name || lastFlight.arrival_airport;
        const flightRoute = departureAirport && arrivalAirport ? `${departureAirport} → ${arrivalAirport}` : '';
        // Get return flight date if exists
        const returnDate = flightItem.journey?.returning?.flights?.length
          ? new Date(
              Math.max(
                ...flightItem.journey.returning.flights.map((flight) => new Date(flight.departing_date).getTime()),
              ),
            )
          : undefined;

        orderItemsAggregate.push({
          name: flightRoute,
          type: flightItem.type,
          status: flightItem.status,
          price: getItemPrice(flightItem.total),
          // there could be multiple flights, hence select the earliest one.
          date: new Date(Math.min(...departingFlights.map((flight) => new Date(flight.departing_date).getTime()))),
          endDate: returnDate,
        });
      }
    });
  }

  if (order.has_insurance) {
    order.insurance_items.forEach((insurance) => {
      orderItemsAggregate.push({
        name: '',
        type: insurance.type,
        status: insurance.status,
        price: getItemPrice(insurance.total),
        date: insurance.start_date ? new Date(insurance.start_date) : undefined,
        endDate: insurance.end_date ? new Date(insurance.end_date) : undefined,
      });
    });
  }

  if (order.has_tour) {
    order.tour_items.forEach((tour) => {
      orderItemsAggregate.push({
        name: tour.tour_name,
        type: tour.type,
        status: tour.status,
        price: getItemPrice(tour.total),
        date: tour.start_date ? new Date(tour.start_date) : undefined,
        endDate: tour.end_date ? new Date(tour.end_date) : undefined,
      });
    });
  }

  if (order.cruise_items.length > 0) {
    order.cruise_items.forEach((cruiseItem) => {
      orderItemsAggregate.push({
        name: cruiseItem.offer_name,
        type: cruiseItem.type || 'cruise',
        status: cruiseItem.status,
        price: getItemPrice(cruiseItem.total),
        date: cruiseItem.departure_date ? new Date(cruiseItem.departure_date) : undefined,
        endDate: undefined,
      });
    });
  }

  if (order.car_hire_items.length > 0) {
    order.car_hire_items.forEach((carHire) => {
      const returnDate = (carHire.reservation?.return as any)?.date;
      orderItemsAggregate.push({
        name: '',
        type: carHire.type,
        status: carHire.status,
        price: getItemPrice(carHire.total),
        date: new Date(carHire.reservation?.pickUp?.date),
        endDate: returnDate ? new Date(returnDate) : undefined,
      });
    });
  }

  if (order.custom_offer_items.length > 0) {
    order.custom_offer_items.forEach((customOfferItem) => {
      const customItem = customOfferItem.custom_offer?.items?.[0];
      if (customItem) {
        orderItemsAggregate.push({
          name: customItem.name,
          type: customItem.type,
          isCustomOffer: true,
          status: customOfferItem.status || 'pending',
          price: getItemPrice(customOfferItem.total),
          date: customItem.start_date ? new Date(customItem.start_date) : undefined,
          endDate: customItem.end_date ? new Date(customItem.end_date) : undefined,
        });
      }
    });
  }

  return orderItemsAggregate;
}
